import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../utils/config';
import { useTranslation } from '../context/TranslationContext';
import { Mail, MessageSquare, User, Building, Calendar, Eye, X, Send, Search, Filter, MoreVertical, Phone, Video, Info, Paperclip, Clock } from 'lucide-react';
import './SentEmailsDashboard.css';

interface EmailRecord {
  _id: string;
  request_id: string;
  supplier_index: number;
  supplier_email: string;
  supplier_name: string;
  direction: 'sent' | 'received';
  subject: string;
  content: string;
  sent_at: string;
  sent_by: string;
  status: 'sent' | 'failed';
  response: string;
  zepto_email_id?: string;
  attachments?: Array<{
    filename?: string;
    name?: string;
    content_type: string;
    size?: number;
    file_size?: number;
    attachment_id?: string;
  }>;
  tracking?: {
    opened: boolean;
    clicked: boolean;
    sent: boolean;
    opened_at?: string;
    clicked_at?: string;
  };
  tracking_events?: Array<{
    type: string;
    timestamp: string;
    recipient?: string;
    clicked_url?: string;
  }>;
  request?: {
    _id: string;
    product_name: string;
    user_name: string;
    user_email: string;
    user_department: string;
  };
  requester_company?: {
    name: string;
    email: string;
  };
}

interface EmailHistory {
  request: {
    _id: string;
    product_name: string;
    user_name: string;
    user_email: string;
    user_department: string;
  };
  supplier: {
    company_name: string;
    email: string;
  };
  email_history: EmailRecord[];
}

interface GroupedEmails {
  request_id: string;
  request_name: string;
  requester_name: string;
  requester_company: string;
  emails: EmailRecord[];
  total_emails: number;
  last_email_date: string;
  last_message: string;
  unread_count: number;
  query_id: string;
  has_attachments: boolean;
}

const SentEmailsDashboard: React.FC = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [emails, setEmails] = useState<EmailRecord[]>([]);
  const [groupedEmails, setGroupedEmails] = useState<GroupedEmails[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState<GroupedEmails | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<EmailHistory | null>(null);
  const [emailHistoryModal, setEmailHistoryModal] = useState(false);
  const [chatModal, setChatModal] = useState(false);
  const [replyModal, setReplyModal] = useState(false);
  const [replyData, setReplyData] = useState({
    subject: '',
    content: '',
    replyToEmailId: '',
    attachments: [] as File[]
  });
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'sent' | 'received'>('all');
  const [testEmailModal, setTestEmailModal] = useState(false);
  const [testEmailData, setTestEmailData] = useState({
    to_email: 'admin@gordiananalytics.com',
    subject: 'Test Email from Sent Emails Dashboard',
    content: '<h1>Test Email</h1><p>This is a test email sent from the Sent Emails Dashboard using production Mailgun domain.</p><p><strong>Reply to this email to test the reply tracking system!</strong></p>',
    request_id: '68a74753ab40cea5e57d5b91',
    supplier_index: 4
  });
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  useEffect(() => {
    if (token) {
      fetchSentEmails();
    }
  }, [token]);

  // Function to strip HTML tags and extract readable text
  const stripHtmlAndFormat = (htmlContent: string): string => {
    if (!htmlContent) return '';
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Extract text content
    let textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // Clean up extra whitespace and newlines
    textContent = textContent
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/\n\s*\n/g, '\n') // Replace multiple newlines with single newline
      .trim();
    
    // Extract key information from the content
    const lines = textContent.split('\n');
    const keyInfo: string[] = [];
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine && 
          !trimmedLine.includes('Gordian Analytics') && 
          !trimmedLine.includes('Request ID:') && 
          !trimmedLine.includes('Supplier Index:') &&
          !trimmedLine.includes('This email was sent') &&
          !trimmedLine.includes('Please do not reply') &&
          !trimmedLine.includes('Reply to:') &&
          !trimmedLine.includes('Sehr geehrte') &&
          !trimmedLine.includes('Mit freundlichen') &&
          !trimmedLine.includes('[Ihr Name]') &&
          !trimmedLine.includes('[Ihre Position]') &&
          !trimmedLine.includes('[Ihre Firma]') &&
          !trimmedLine.includes('wir interessieren uns') &&
          !trimmedLine.includes('wir würden uns freuen') &&
          !trimmedLine.includes('Zusätzliche Anmerkungen')) {
        keyInfo.push(trimmedLine);
      }
    }
    
    // Look for product information specifically
    const productInfo = keyInfo.find(line => 
      line.includes('Produktname') || 
      line.includes('Produktmarke') || 
      line.includes('Menge') || 
      line.includes('Budget') ||
      line.includes('Monitor') ||
      line.includes('Dell')
    );
    
    if (productInfo) {
      return productInfo;
    }
    
    // Return first meaningful line or default message
    return keyInfo[0] || 'Email content';
  };

  const fetchSentEmails = async () => {
    if (!token) {
      console.error('No token available');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.SENT_EMAILS, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const emailsData = data.data || [];
        setEmails(emailsData);
        
        // Group emails by request_id
        const grouped = groupEmailsByRequest(emailsData);
        setGroupedEmails(grouped);
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Failed to fetch sent emails:', errorData);
        setError(errorData.message || 'Failed to fetch sent emails');
      }
    } catch (error) {
      console.error('Error fetching sent emails:', error);
      setError('Error fetching sent emails. Please check if the backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const groupEmailsByRequest = (emailsData: EmailRecord[]): GroupedEmails[] => {
    const grouped: { [key: string]: GroupedEmails } = {};

    emailsData.forEach(email => {
      const requestId = email.request_id;
      
      if (!grouped[requestId]) {
        grouped[requestId] = {
          request_id: requestId,
          request_name: email.request?.product_name || 'Unknown Product',
          requester_name: email.request?.user_name || 'Unknown User',
          requester_company: email.requester_company?.name || 'Unknown Company',
          emails: [],
          total_emails: 0,
          last_email_date: '',
          last_message: '',
          unread_count: 0,
          query_id: requestId.substring(0, 8),
          has_attachments: false
        };
      }

      grouped[requestId].emails.push(email);
      grouped[requestId].total_emails += 1;

      // Update last email date and message
      const emailDate = new Date(email.sent_at);
      const currentLastDate = grouped[requestId].last_email_date ? new Date(grouped[requestId].last_email_date) : new Date(0);
      
      if (emailDate > currentLastDate) {
        grouped[requestId].last_email_date = email.sent_at;
        // Use the new function to format the message
        const formattedMessage = stripHtmlAndFormat(email.content);
        grouped[requestId].last_message = formattedMessage.substring(0, 80) + (formattedMessage.length > 80 ? '...' : '');
      }
    });

    // Convert to array and sort by last email date
    return Object.values(grouped).sort((a, b) => 
      new Date(b.last_email_date).getTime() - new Date(a.last_email_date).getTime()
    );
  };

  const fetchEmailHistory = async (requestId: string, supplierIndex: number) => {
    if (!token) {
      console.error('No token available');
      return;
    }

    try {
      console.log(`Fetching email history for request: ${requestId}, supplier: ${supplierIndex}`);
      
      const response = await fetch(API_ENDPOINTS.EMAIL_HISTORY(requestId, supplierIndex.toString()), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Email history data:', data);
        setSelectedEmail(data.data);
        setChatModal(true); // Changed from setEmailHistoryModal to setChatModal
      } else {
        console.error('Failed to fetch email history:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Error fetching email history:', error);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      // Reply-to adresi oluştur
      const replyTo = `reply-${testEmailData.request_id}-${testEmailData.supplier_index}@sales.gordiananalytics.com`;
      
      const emailPayload = {
        to_email: testEmailData.to_email,
        subject: testEmailData.subject,
        content: testEmailData.content,
        reply_to: replyTo,
        request_id: testEmailData.request_id,
        supplier_index: testEmailData.supplier_index
      };

      const response = await fetch(API_ENDPOINTS.MAILGUN_SEND_TEST_EMAIL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(emailPayload)
      });

      const result = await response.json();

      if (response.ok) {
        alert(`✅ Test email sent successfully!\nMessage ID: ${result.message_id}\n\n📧 Reply-To: ${replyTo}\n\n💡 Now reply to this email to test reply tracking!`);
        setTestEmailModal(false);
        // Refresh emails list
        fetchSentEmails();
      } else {
        alert(`❌ Failed to send test email: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      alert('❌ Error sending test email. Please check console for details.');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      setReplyData({ ...replyData, attachments: fileArray });
    }
  };

  const removeAttachment = (index: number) => {
    const newAttachments = replyData.attachments.filter((_, i) => i !== index);
    setReplyData({ ...replyData, attachments: newAttachments });
  };

  const downloadAttachment = async (attachment: any, emailId: string) => {
    if (!token) {
      console.error('No token available');
      return;
    }

    try {
      // Try to download from supplier_product_documents first
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/supplier-products/download-document/${attachment.attachment_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.filename || attachment.name || 'attachment';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // Fallback: try Mailgun attachment download
        const mailgunResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/mailgun/attachments/${attachment.attachment_id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (mailgunResponse.ok) {
          const blob = await mailgunResponse.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = attachment.filename || attachment.name || 'attachment';
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          alert('Failed to download attachment');
        }
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Error downloading attachment');
    }
  };

  const sendReply = async () => {
    if (!token) {
      console.error('No token available');
      return;
    }

    if (!selectedEmail || !replyData.subject || !replyData.content) return;

    try {
      // Create FormData for file uploads
      const formData = new FormData();
      formData.append('request_id', selectedEmail.request._id);
      formData.append('supplier_index', (selectedEmail.email_history[0]?.supplier_index || 0).toString());
      formData.append('subject', replyData.subject);
      formData.append('content', replyData.content);
      formData.append('reply_to_email_id', replyData.replyToEmailId);
      
      // Add attachments
      replyData.attachments.forEach((file, index) => {
        formData.append(`attachment_${index}`, file);
      });

      const response = await fetch(API_ENDPOINTS.SEND_REPLY, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type header, let browser set it with boundary for FormData
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        alert('Reply sent successfully!');
        setReplyModal(false);
        setReplyData({ subject: '', content: '', replyToEmailId: '', attachments: [] });
        // Refresh email history
        if (selectedEmail) {
          fetchEmailHistory(selectedEmail.request._id, selectedEmail.email_history[0]?.supplier_index || 0);
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to send reply: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Error sending reply');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    // Date objects store time in UTC milliseconds, so direct comparison is correct
    // This ensures we're using database UTC time consistently
    const diffInMilliseconds = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} min${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hr${diffInHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    }
  };

  const formatFullDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    return status === 'sent' ? '#10b981' : '#ef4444';
  };

  const getStatusIcon = (status: string) => {
    return status === 'sent' ? <Mail className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />;
  };

  const filteredEmails = groupedEmails.filter(email => {
    const matchesSearch = email.request_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         email.requester_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         email.requester_company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         email.query_id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'sent' && email.emails.some(e => e.direction === 'sent')) ||
                         (filterStatus === 'received' && email.emails.some(e => e.direction === 'received'));
    
    return matchesSearch && matchesFilter;
  });

  const handleRowClick = async (email: GroupedEmails) => {
    // Get the first email to extract request_id and supplier_index
    const firstEmail = email.emails[0];
    if (firstEmail && firstEmail.request_id && firstEmail.supplier_index !== undefined) {
      try {
        // Fetch detailed email history
        await fetchEmailHistory(firstEmail.request_id, firstEmail.supplier_index);
      } catch (error) {
        console.error('Error fetching email history:', error);
        // Fallback to basic data
        setSelectedEmail({
          request: {
            _id: email.request_id,
            product_name: email.request_name,
            user_name: email.requester_name,
            user_email: '',
            user_department: ''
          },
          supplier: {
            company_name: email.requester_company,
            email: ''
          },
          email_history: email.emails
        });
        setChatModal(true);
      }
    } else {
      // Fallback if no email data
      setSelectedEmail({
        request: {
          _id: email.request_id,
          product_name: email.request_name,
          user_name: email.requester_name,
          user_email: '',
          user_department: ''
        },
        supplier: {
          company_name: email.requester_company,
          email: ''
        },
        email_history: email.emails
      });
      setChatModal(true);
    }
  };

  if (loading) {
    return (
      <div className="emails-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('common.loading_emails')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="emails-dashboard">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchSentEmails} className="retry-btn">
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="emails-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div>
            <h1>{t('common.sent_emails')}</h1>
            <p>{t('common.view_manage_emails')}</p>
          </div>
          <div className="header-actions">
            <div className="search-box">
              <Search className="w-4 h-4 search-icon" />
              <input
                type="text"
                placeholder="Search emails..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-buttons">
              <button
                className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                onClick={() => setFilterStatus('all')}
              >
                All
              </button>
              <button
                className={`filter-btn ${filterStatus === 'sent' ? 'active' : ''}`}
                onClick={() => setFilterStatus('sent')}
              >
                Sent
              </button>
              <button
                className={`filter-btn ${filterStatus === 'received' ? 'active' : ''}`}
                onClick={() => setFilterStatus('received')}
              >
                Received
              </button>
            </div>
            <button
              className="test-email-btn"
              onClick={() => setTestEmailModal(true)}
              title="Send test email using Mailgun sandbox"
            >
              <Send className="w-4 h-4" />
              Test Email
            </button>
          </div>
        </div>
      </div>

      {/* Emails Table */}
      <div className="emails-table-container">
        <table className="emails-table">
          <thead>
            <tr>
              <th className="received-col">RECEIVED</th>
              <th className="customer-col">CUSTOMER</th>
              <th className="query-col">QUERY ID</th>
              <th className="subject-col">SUBJECT</th>
              <th className="message-col">MESSAGE</th>
              <th className="actions-col">ACTIONS</th>
              <th className="time-col">TIME</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmails.length === 0 ? (
              <tr>
                <td colSpan={7} className="no-data">
                  <div className="no-emails">
                    <Mail className="w-12 h-12" />
                    <h3>{t('common.no_emails_found')}</h3>
                    <p>{t('common.start_sending_emails')}</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredEmails.map((email, index) => (
                <tr 
                  key={email.request_id} 
                  className={`email-row ${selectedChat?.request_id === email.request_id ? 'selected' : ''}`}
                  onClick={() => handleRowClick(email)}
                >
                  <td className="received-col">{formatDate(email.last_email_date)}</td>
                  <td className="customer-col">
                    <div className="customer-info">
                      <span className="customer-name">{email.requester_company}</span>
                      <span className="requester-name">{email.requester_name}</span>
                    </div>
                  </td>
                  <td className="query-col">{email.query_id}</td>
                  <td className="subject-col">{email.request_name}</td>
                  <td className="message-col">{email.last_message}</td>
                  <td className="actions-col">
                    <div className="action-buttons">
                      {email.has_attachments && (
                        <button className="action-btn attachment-btn">
                          <Paperclip className="w-4 h-4" />
                        </button>
                      )}
                      <button className="action-btn chat-btn">
                        <MessageSquare className="w-4 h-4" />
                        {email.unread_count > 0 && (
                          <span className="unread-dot"></span>
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="time-col">{formatTimeAgo(email.last_email_date)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Chat Modal */}
      {chatModal && selectedEmail && (
        <div className="modal-overlay">
          <div className="chat-modal">
            <div className="modal-header">
              <div className="chat-contact-info">
                <div className="contact-avatar">
                  <Building className="w-6 h-6" />
                </div>
                <div className="contact-details">
                  <h3>{selectedEmail.request?.product_name || 'Unknown Product'}</h3>
                  <p>{selectedEmail.request?.user_name || 'Unknown User'} • {selectedEmail.supplier?.company_name || 'Unknown Company'}</p>
                </div>
              </div>
              <button
                onClick={() => setChatModal(false)}
                className="close-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="messages-container">
              {selectedEmail.email_history?.map((email, index) => (
                <div
                  key={email._id}
                  className={`message ${email.direction === 'sent' ? 'sent' : 'received'}`}
                >
                  <div className="message-content">
                    <div className="message-header">
                      <span className="message-sender">
                        {email.direction === 'sent' ? 'You' : email.supplier_name}
                      </span>
                      <span className="message-time">{formatTimeAgo(email.sent_at)}</span>
                    </div>
                    <div className="message-subject">{email.subject}</div>
                    <div className="message-text" dangerouslySetInnerHTML={{ __html: email.content }}></div>
                    <div className="message-status">
                      {email.direction === 'sent' && (
                        <div className="status-indicator">
                          {getStatusIcon(email.status)}
                          <span style={{ color: getStatusColor(email.status) }}>
                            {email.status}
                          </span>
                        </div>
                      )}
                      {email.tracking && email.tracking.opened && (
                        <div className="tracking-indicator">
                          <Eye className="w-3 h-3" />
                          <span>Opened</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="reply-area">
              <div className="reply-input-container">
                <textarea
                  placeholder="Type a message..."
                  className="reply-input"
                  rows={1}
                />
                <button className="send-btn">
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email History Modal */}
      {emailHistoryModal && selectedEmail && (
        <div className="modal-overlay">
          <div className="email-history-modal">
            <div className="modal-header">
              <h3>Email History</h3>
              <button
                onClick={() => {
                  setEmailHistoryModal(false);
                  setSelectedEmail(null);
                }}
                className="close-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="modal-content">
              <div className="request-info">
                <h4>Request Details</h4>
                <div className="info-grid">
                  <div>
                    <span className="label">Product:</span> {selectedEmail.request.product_name}
                  </div>
                  <div>
                    <span className="label">Requester:</span> {selectedEmail.request.user_name}
                  </div>
                  <div>
                    <span className="label">Supplier:</span> {selectedEmail.supplier.company_name}
                  </div>
                  <div>
                    <span className="label">Supplier Email:</span> {selectedEmail.supplier.email}
                  </div>
                </div>
              </div>

              <div className="email-history">
                <h4>Email History</h4>
                <div className="history-list">
                  {selectedEmail.email_history.map((email, index) => (
                    <div
                      key={email._id}
                      className={`history-item ${email.direction === 'sent' ? 'sent' : 'received'}`}
                    >
                      <div className="history-header">
                        <div className="direction-badge">
                          {email.direction === 'sent' ? 'Sent' : 'Received'}
                        </div>
                        <div className="email-date">{formatFullDate(email.sent_at)}</div>
                        {email.direction === 'received' && (
                          <button
                            onClick={() => {
                              setReplyData({
                                subject: `Re: ${email.subject}`,
                                content: '',
                                replyToEmailId: email._id,
                                attachments: []
                              });
                              setReplyModal(true);
                            }}
                            className="reply-btn"
                          >
                            Reply
                          </button>
                        )}
                      </div>
                      
                      <div className="email-subject">{email.subject}</div>
                      
                      <div className="email-content">
                        <div className="content-text" dangerouslySetInnerHTML={{ __html: email.content }}></div>
                        
                        {/* Attachments */}
                        {email.attachments && email.attachments.length > 0 && (
                          <div className="message-attachments">
                            <div className="attachments-header">📎 Attachments:</div>
                            {email.attachments.map((attachment: any, attIndex: number) => {
                              console.log('🔍 DEBUG: Attachment data:', attachment);
                              return (
                                <div key={attIndex} className="attachment-item">
                                  <div className="attachment-info">
                                    <span className="attachment-name">{attachment.filename || attachment.name || 'Unknown file'}</span>
                                    <span className="attachment-size">({Math.round((attachment.size || attachment.file_size || 0) / 1024)} KB)</span>
                                  </div>
                                  <button 
                                    onClick={() => downloadAttachment(attachment, email._id)}
                                    className="attachment-download"
                                  >
                                    📥 Download
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button
                  onClick={() => {
                    setReplyData({
                      subject: '',
                      content: '',
                      replyToEmailId: '',
                      attachments: []
                    });
                    setReplyModal(true);
                  }}
                  className="send-new-btn"
                >
                  Send New Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {replyModal && (
        <div className="modal-overlay">
          <div className="reply-modal">
            <div className="modal-header">
              <h3>Send Reply</h3>
              <button
                onClick={() => setReplyModal(false)}
                className="close-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="modal-content">
              <div className="form-group">
                <label>Subject</label>
                <input
                  type="text"
                  value={replyData.subject}
                  onChange={(e) => setReplyData({ ...replyData, subject: e.target.value })}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Content</label>
                <textarea
                  value={replyData.content}
                  onChange={(e) => setReplyData({ ...replyData, content: e.target.value })}
                  rows={8}
                  className="form-textarea"
                />
              </div>

              <div className="form-group">
                <label>Attachments</label>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="form-input"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                />
                {replyData.attachments.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 mb-2">Selected files:</p>
                    {replyData.attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-100 p-2 rounded mb-1">
                        <span className="text-sm">{file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => setReplyModal(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={sendReply}
                className="send-btn"
              >
                Send Reply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {testEmailModal && (
        <div className="modal-overlay" onClick={() => setTestEmailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🧪 Send Test Email (Sandbox)</h2>
              <button 
                className="close-btn" 
                onClick={() => setTestEmailModal(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>To Email (Authorized Recipient):</label>
                <input
                  type="email"
                  value={testEmailData.to_email}
                  onChange={(e) => setTestEmailData({
                    ...testEmailData,
                    to_email: e.target.value
                  })}
                  placeholder="deniz.kara@gordiananalytics.com"
                  className="form-input"
                />
                <small className="form-help">
                  Sandbox domain only allows sending to authorized recipients
                </small>
              </div>
              
              <div className="form-group">
                <label>Subject:</label>
                <input
                  type="text"
                  value={testEmailData.subject}
                  onChange={(e) => setTestEmailData({
                    ...testEmailData,
                    subject: e.target.value
                  })}
                  placeholder="Test Email Subject"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Content (HTML):</label>
                <textarea
                  value={testEmailData.content}
                  onChange={(e) => setTestEmailData({
                    ...testEmailData,
                    content: e.target.value
                  })}
                  placeholder="<h1>Test Email</h1><p>This is a test email...</p>"
                  className="form-textarea"
                  rows={8}
                />
                <small className="form-help">
                  You can use HTML tags for formatting
                </small>
              </div>
              
              <div className="form-group">
                <label>Request ID (for reply tracking):</label>
                <input
                  type="text"
                  value={testEmailData.request_id}
                  onChange={(e) => setTestEmailData({
                    ...testEmailData,
                    request_id: e.target.value
                  })}
                  placeholder="68a74753ab40cea5e57d5b91"
                  className="form-input"
                />
                <small className="form-help">
                  Used to create reply-to address for tracking
                </small>
              </div>
              
              <div className="form-group">
                <label>Supplier Index:</label>
                <input
                  type="number"
                  value={testEmailData.supplier_index}
                  onChange={(e) => setTestEmailData({
                    ...testEmailData,
                    supplier_index: parseInt(e.target.value) || 0
                  })}
                  placeholder="4"
                  className="form-input"
                />
                <small className="form-help">
                  Supplier index for reply tracking
                </small>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setTestEmailModal(false)}
                disabled={sendingTestEmail}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSendTestEmail}
                disabled={sendingTestEmail}
              >
                {sendingTestEmail ? (
                  <>
                    <div className="spinner-small"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Test Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SentEmailsDashboard; 